import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get, getErrorMessage, patch, post } from '../api/client';
import LeadersSurveyView from './LeadersSurveyView';
import './LeadersSurveyPage.css';

const LeadersSurveyPage = () => {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    get('/api/leaders-survey/mine')
      .then((response) => {
        if (cancelled) return;
        if (!response.ok) {
          setError(getErrorMessage(response, 'Failed to load survey'));
          setLoading(false);
          return;
        }
        setData(response.data);
        setProgress(response.data?.progress || null);
        setReadOnly(Boolean(response.data?.readOnly));
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load survey');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!data?.modules?.length) return;
    const withQuestions = data.modules.filter((mod) => mod.subsections?.some((section) => section.questions?.length));
    if (!withQuestions.length) return;
    const moduleKey = params.get('module');
    const sectionCode = params.get('section');
    const selected = withQuestions.find((mod) => mod.key === moduleKey);
    const sectionOk = selected?.subsections?.some((section) => section.code === sectionCode);
    if (selected && sectionOk) return;
    const resumeModule = withQuestions.find((mod) => mod.key === data.resume?.module) || withQuestions[0];
    const resumeSection = resumeModule.subsections.find((section) => section.code === data.resume?.subsection)
      || resumeModule.subsections[0];
    setParams({ module: resumeModule.key, section: resumeSection.code }, { replace: true });
  }, [data, params, setParams]);

  const onNavigate = useCallback((moduleKey, sectionCode) => {
    setParams({ module: moduleKey, section: sectionCode });
    window.scrollTo(0, 0);
  }, [setParams]);

  const onSave = useCallback(async (questionId, payload) => {
    const response = await patch('/api/leaders-survey/answers', {
      questionId,
      value: payload.value,
      fillIn: payload.fillIn,
    });
    if (response.status === 409) setReadOnly(true);
    if (!response.ok) throw new Error('save failed');
    if (response.data?.progress) setProgress(response.data.progress);
  }, []);

  const onMarkComplete = useCallback(async (moduleKey) => {
    const response = await post(`/api/leaders-survey/modules/${moduleKey}/complete`);
    if (response.status === 409) setReadOnly(true);
    if (!response.ok) throw new Error('complete failed');
    if (response.data?.progress) setProgress(response.data.progress);
  }, []);

  if (loading) {
    return <div className="my-survey"><p>Loading…</p></div>;
  }

  if (error) {
    return (
      <div className="my-survey">
        <PageHeader title="My Survey" section="leaders" />
        <p className="my-survey-error" role="alert">{error}</p>
      </div>
    );
  }

  if (data?.setupRequired) {
    return (
      <div className="my-survey">
        <PageHeader title="My Survey" section="leaders" />
        <p>The survey isn&apos;t set up yet.</p>
      </div>
    );
  }

  if (!data?.available) {
    return (
      <div className="my-survey">
        <PageHeader title="My Survey" section="leaders" />
        <p>The leaders survey isn&apos;t available yet.</p>
      </div>
    );
  }

  if (!data.modules?.length) {
    return (
      <div className="my-survey">
        <PageHeader title="My Survey" section="leaders" />
        <p>You don&apos;t have any modules assigned yet. A ministry lead can assign them to you.</p>
      </div>
    );
  }

  if (!data.modules.some((mod) => mod.subsections?.some((section) => section.questions?.length))) {
    return (
      <div className="my-survey">
        <PageHeader title="My Survey" section="leaders" />
        <p>No questions to answer right now.</p>
      </div>
    );
  }

  const moduleKey = params.get('module');
  const sectionCode = params.get('section');
  const ready = data.modules.some(
    (mod) => mod.key === moduleKey && mod.subsections?.some((section) => section.code === sectionCode && section.questions?.length)
  );
  if (!ready) return <div className="my-survey"><p>Loading…</p></div>;

  return (
    <>
      <div className="my-survey my-survey-header-wrap">
        <PageHeader title="My Survey" section="leaders" />
      </div>
      <LeadersSurveyView
        title={data.title}
        readOnly={readOnly}
        modules={data.modules}
        initialAnswers={data.answers}
        progress={progress}
        activeModuleKey={moduleKey}
        activeSectionCode={sectionCode}
        resumeQuestionId={data.resume?.questionId}
        onNavigate={onNavigate}
        onSave={onSave}
        onMarkComplete={onMarkComplete}
      />
    </>
  );
};

export default LeadersSurveyPage;
